import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, ArrowUp, ArrowDown, DownloadSimple, EyeSlash, Eye, Plus, Trash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ExcelExportPreviewModal
 * ------------------------
 * Small preview shown BEFORE the actual Excel/PDF download so the user can:
 *   1. See a live top-5-row preview of exactly what will be exported
 *   2. Reorder columns with ↑/↓ arrows (drag-drop is overkill on mobile)
 *   3. Toggle any column off with the eye icon — hidden columns are excluded
 * When they hit Download, we hand back the final `columns` key list (comma-
 * separated) that the parent forwards to the backend export endpoint.
 *
 * Point 34 — the same preview (reorder + hide/show) now also drives the PDF
 * export via the `format` prop, not just Excel.
 * Point 33 — only an admin (`isAdmin`) can actually reorder/hide columns;
 * handlers see a read-only preview using whatever admin last saved.
 * Point 35 — admin's order/visibility is saved server-side (GET/PUT
 * /report-export/column-prefs) so it's the shared default for every export,
 * on every device, not just the browser that set it. A localStorage copy is
 * kept only as an instant-paint cache (point 23 pattern) ahead of that fetch.
 *
 * Props:
 *   - open, onClose
 *   - format: 'xlsx' | 'pdf' (default 'xlsx')
 *   - rows: array of report rows (only the first 5 are previewed)
 *   - isAdmin: whether the current user may edit column order/visibility
 *   - onDownload({ columns })    → parent triggers the actual export
 */
const COL_PREFS_KEY = 'report_export_column_prefs_v1';

const loadCachedColumnPrefs = () => {
  try {
    const raw = localStorage.getItem(COL_PREFS_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved) || saved.length === 0) return null;
    return saved;
  } catch (_) { return null; }
};

const mergeColumnPrefs = (saved) => {
  if (!saved || saved.length === 0) return ALL_COLUMNS.map((c) => ({ ...c, visible: true }));
  // Issue 2 — preserve admin label overrides + admin-created custom columns.
  const byKey = Object.fromEntries(ALL_COLUMNS.map(c => [c.key, c]));
  const merged = saved
    .filter(s => byKey[s.key] || s.custom)
    .map(s => s.custom
      ? { key: s.key, label: s.label || 'Custom Column', visible: s.visible !== false, custom: true }
      : { ...byKey[s.key], label: s.label || byKey[s.key].label, visible: s.visible !== false });
  const seen = new Set(merged.map(c => c.key));
  ALL_COLUMNS.forEach(c => { if (!seen.has(c.key)) merged.push({ ...c, visible: true }); });
  return merged;
};
const ALL_COLUMNS = [
  { key: 'sn',       label: '#' },
  // Session-48g: Client Code (SN0xx) surfaced as 2nd column in every report.
  { key: 'client_code', label: 'Client Code' },
  { key: 'username', label: 'Username' },
  { key: 'mb',       label: 'MB' },
  { key: 'type',     label: 'Type' },
  { key: 'amount',   label: 'Amount' },
  { key: 'date',     label: 'Date (IST)' },
  { key: 'time',     label: 'Time (IST)' },
  { key: 'status',   label: 'Status' },
  { key: 'utr',      label: 'UTR' },
  { key: 'ref',      label: 'Reference' },
  { key: 'rf',       label: 'Received From (Client Account)' },
  { key: 'ri',       label: 'Received In (Our Account)' },
  { key: 'pt',       label: 'Paid To (Client Account)' },
  { key: 'pf',       label: 'Paid From (Our Account)' },
  { key: 'desc',     label: 'Description' },
  { key: 'addr',     label: 'Client Address (from IFSC)' },
  // Session-48d Reports Upgrade K — 3 timestamps + 2 TB columns.
  { key: 'created_time',  label: 'Created (Customer)' },
  { key: 'approved_time', label: 'First Reply (Handler)' },
  { key: 'closed_time',   label: 'Closed' },
  { key: 'tb1',           label: 'TB1, min (Create → First Reply)' },
  { key: 'tb2',           label: 'TB2, min (First Reply → Close)' },
  { key: 'tb3',           label: 'TB3, min (Create → Close)' },
  // Session-48d — withdraw-only account snapshot (frozen at approval time)
  { key: 'acc_no',     label: 'Account Number (Withdraw)' },
  { key: 'acc_ifsc',   label: 'IFSC Code (Withdraw)' },
  { key: 'acc_holder', label: 'Account Holder (Withdraw)' },
  // Legacy timing columns kept for backward compat.
  { key: 'req_time',  label: 'Request Time (IST)' },
  { key: 'paid_time', label: 'Approved Time (IST)' },
  { key: 'tb',        label: 'Time Between, min (T/B)' },
];

const ExcelExportPreviewModal = ({ open, onClose, rows = [], onDownload, format = 'xlsx', isAdmin = false }) => {
  const [cols, setCols] = useState(() => mergeColumnPrefs(loadCachedColumnPrefs()));
  const skipNextSaveRef = useRef(false);

  // Point 35 — instant paint from the local cache, then reconcile against
  // the server's shared default as soon as the preview opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/report-export/column-prefs`).then((res) => {
      if (cancelled) return;
      const serverCols = res.data?.columns;
      if (Array.isArray(serverCols) && serverCols.length > 0) {
        skipNextSaveRef.current = true; // this came FROM the server — don't PUT it right back
        setCols(mergeColumnPrefs(serverCols));
        try { localStorage.setItem(COL_PREFS_KEY, JSON.stringify(serverCols)); } catch (_) {}
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  // Point 33/35 — only admin's edits are persisted (server + local cache);
  // a handler's read-only view never writes back.
  useEffect(() => {
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    if (!isAdmin) return;
    const prefs = cols.map(c => ({ key: c.key, visible: c.visible, label: c.label, custom: !!c.custom }));
    try { localStorage.setItem(COL_PREFS_KEY, JSON.stringify(prefs)); } catch (_) {}
    const t = setTimeout(() => {
      axios.put(`${API}/report-export/column-prefs`, { columns: prefs }).catch(() => {});
    }, 500); // debounce rapid ↑/↓/eye clicks into one save
    return () => clearTimeout(t);
  }, [cols, isAdmin]);

  const move = (idx, delta) => {
    if (!isAdmin) return;
    setCols(prev => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return next;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const toggle = (idx) => {
    if (!isAdmin) return;
    setCols(prev => prev.map((c, i) => i === idx ? { ...c, visible: !c.visible } : c));
  };
  // Issue 2 — admin-only rename + custom blank columns.
  const rename = (idx, label) => {
    if (!isAdmin) return;
    setCols(prev => prev.map((c, i) => i === idx ? { ...c, label } : c));
  };
  const addCustom = () => {
    if (!isAdmin) return;
    const key = `custom_${Date.now()}`;
    setCols(prev => [...prev, { key, label: 'New Column', visible: true, custom: true }]);
  };
  const removeCustom = (idx) => {
    if (!isAdmin) return;
    setCols(prev => prev.filter((_, i) => i !== idx));
  };

  const visibleCols = useMemo(() => cols.filter(c => c.visible), [cols]);

  const cellValue = (r, key, i) => {
    if (!r) return '-';
    const isDep = r.type === 'deposit';
    switch (key) {
      case 'sn': return String(i + 1);
      case 'username': return r.customer_username || '-';
      case 'client_code': return r.client_code || '-';
      case 'mb': return r.customer_mb || '-';
      case 'type': return (r.type || '').toString().charAt(0).toUpperCase() + (r.type || '').slice(1);
      case 'amount': return `₹${r.amount || 0}`;
      case 'date': return r.effective_at ? new Date(r.effective_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : (r.created_at || '').slice(0, 10);
      case 'time': {
        const t = r.effective_at || r.created_at;
        if (!t) return '-';
        try { return new Date(t).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
      }
      case 'status': return r.status || (isDep ? 'done' : '-');
      case 'utr': return r.utr_number || '-';
      case 'ref': return r.paid_reference || r.reference || '-';
      case 'rf': return isDep ? (r.received_from || '-') : '-';
      case 'ri': return isDep ? (r.received_in || r.bank_account || '-') : '-';
      case 'pt': return !isDep ? ((r.bank_snapshot && r.bank_snapshot.bank_name) || r.paid_bank_account || r.bank_account || '-') : '-';
      case 'pf': return !isDep ? (r.paid_from_bank || '-') : '-';
      case 'desc': return r.description || '-';
      case 'addr': return r.client_address_text || (r.client_address && r.client_address.formatted) || '-';
      // Session-32 timing columns — deposits blank, pending/cancelled → '-'.
      case 'req_time': {
        if (isDep || !r.created_at) return isDep ? '' : '-';
        try {
          const d = new Date(r.created_at);
          const dd = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' });
          const tt = d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
          return `${dd} ${tt}`;
        } catch { return '-'; }
      }
      case 'paid_time': {
        if (isDep) return '';
        if (!r.paid_at) return '-';
        try {
          const d = new Date(r.paid_at);
          const dd = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' });
          const tt = d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
          return `${dd} ${tt}`;
        } catch { return '-'; }
      }
      case 'tb': {
        if (isDep) return '';
        if (r.status !== 'paid' || !r.created_at || !r.paid_at) return '-';
        // Point 38 — Time Between is always shown in minutes, never seconds.
        try {
          const secs = Math.floor((new Date(r.paid_at).getTime() - new Date(r.created_at).getTime()) / 1000);
          if (secs < 0) return '-';
          return `${Math.floor(secs / 60)}m`;
        } catch { return '-'; }
      }
      // Session-48d Reports Upgrade K — 3 timestamps + 2 TB previews.
      case 'created_time': {
        const ts = r.sr_started_at || r.created_at;
        if (!ts) return '-';
        try {
          const d = new Date(ts);
          return `${d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}`;
        } catch { return '-'; }
      }
      case 'approved_time': {
        const ts = r.approved_at || r.created_at;
        if (!ts) return '-';
        try {
          const d = new Date(ts);
          return `${d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}`;
        } catch { return '-'; }
      }
      case 'closed_time': {
        const ts = isDep ? (r.closed_at || r.approved_at || r.created_at) : (r.status === 'paid' ? r.paid_at : null);
        if (!ts) return '-';
        try {
          const d = new Date(ts);
          return `${d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}`;
        } catch { return '-'; }
      }
      // Point 38 — Time Between is always shown in minutes, never seconds.
      case 'tb1': {
        const a = r.sr_started_at || r.created_at;
        const b = r.approved_at || r.created_at;
        if (!a || !b || a === b) return '-';
        try {
          const secs = Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000);
          if (secs < 0) return '-';
          return `${Math.floor(secs / 60)}m`;
        } catch { return '-'; }
      }
      case 'tb2': {
        const a = r.approved_at || r.created_at;
        const b = isDep ? (r.closed_at || r.approved_at || r.created_at) : (r.status === 'paid' ? r.paid_at : null);
        if (!a || !b || a === b) return '-';
        try {
          const secs = Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000);
          if (secs < 0) return '-';
          return `${Math.floor(secs / 60)}m`;
        } catch { return '-'; }
      }
      case 'tb3': {
        const a = r.sr_started_at || r.created_at;
        const b = isDep ? (r.closed_at || r.approved_at || r.created_at) : (r.status === 'paid' ? r.paid_at : null);
        if (!a || !b || a === b) return '-';
        try {
          const secs = Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000);
          if (secs < 0) return '-';
          return `${Math.floor(secs / 60)}m`;
        } catch { return '-'; }
      }
      case 'acc_no':     return isDep ? '' : ((r.bank_snapshot && r.bank_snapshot.bank_account_number) || '-');
      case 'acc_ifsc':   return isDep ? '' : ((r.bank_snapshot && r.bank_snapshot.ifsc_code) || '-');
      case 'acc_holder': return isDep ? '' : ((r.bank_snapshot && r.bank_snapshot.account_holder_name) || '-');
      default: return '-';
    }
  };

  if (!open) return null;

  const isPdf = format === 'pdf';

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="excel-preview-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        <div className={`px-4 py-3 text-white flex items-center justify-between ${isPdf ? 'bg-red-700' : 'bg-green-700'}`}>
          <div>
            <div className="font-bold text-sm">{isPdf ? '📄 PDF Export — Preview' : '📄 Excel Export — Preview'}</div>
            <div className="text-[11px] opacity-90">
              {isAdmin
                ? 'Reorder with ↑↓, hide with the eye icon. Preview updates live.'
                : "Column layout is set by admin — you're viewing it read-only."}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full" data-testid="excel-preview-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0 flex-1 overflow-hidden">
          {/* Column list */}
          <div className="border-r bg-gray-50 overflow-y-auto max-h-[70vh]">
            <div className="text-[11px] font-bold uppercase text-gray-500 px-3 py-2 border-b bg-white sticky top-0">
              Columns ({visibleCols.length}/{cols.length})
            </div>
            {cols.map((c, i) => (
              <div
                key={c.key}
                className={`flex items-center gap-1 px-2 py-1.5 border-b text-xs ${c.visible ? 'bg-white' : 'bg-gray-100 text-gray-400 line-through'}`}
                data-testid={`col-row-${c.key}`}
              >
                {isAdmin && (
                  <>
                    <button
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                      title="Move up"
                      data-testid={`col-up-${c.key}`}
                    ><ArrowUp size={12} weight="bold" /></button>
                    <button
                      onClick={() => move(i, 1)}
                      disabled={i === cols.length - 1}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                      title="Move down"
                      data-testid={`col-down-${c.key}`}
                    ><ArrowDown size={12} weight="bold" /></button>
                    <button
                      onClick={() => toggle(i)}
                      className={`p-1 rounded hover:bg-gray-200 ${c.visible ? 'text-emerald-600' : 'text-gray-400'}`}
                      title={c.visible ? 'Hide' : 'Show'}
                      data-testid={`col-toggle-${c.key}`}
                    >{c.visible ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}</button>
                  </>
                )}
                {!isAdmin && (c.visible ? <Eye size={12} className="text-emerald-600" /> : <EyeSlash size={12} className="text-gray-400" />)}
                {isAdmin ? (
                  <>
                    <input
                      type="text"
                      value={c.label}
                      onChange={(e) => rename(i, e.target.value)}
                      className="flex-1 min-w-0 text-xs px-1.5 py-1 border border-gray-200 rounded focus:outline-none focus:border-emerald-500 bg-white"
                      title="Rename column header"
                      data-testid={`col-rename-${c.key}`}
                    />
                    {c.custom && (
                      <button
                        onClick={() => removeCustom(i)}
                        className="p-1 rounded hover:bg-red-100 text-red-500"
                        title="Remove custom column"
                        data-testid={`col-remove-${c.key}`}
                      ><Trash size={12} weight="bold" /></button>
                    )}
                  </>
                ) : (
                  <span className="flex-1 truncate" title={c.label}>{c.label}</span>
                )}
              </div>
            ))}
            {isAdmin && (
              <button
                onClick={addCustom}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border-t"
                data-testid="col-add-custom"
              >
                <Plus size={12} weight="bold" /> Add custom column
              </button>
            )}
          </div>

          {/* Live preview */}
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-gray-100 text-gray-700 font-bold">
                <tr>
                  {visibleCols.map(c => (
                    <th key={c.key} className="px-2 py-1.5 text-left border whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={r.id || i} className={i % 2 ? 'bg-white' : 'bg-gray-50'}>
                    {visibleCols.map(c => (
                      <td key={c.key} className="px-2 py-1 border truncate max-w-[160px]" title={c.custom ? '' : cellValue(r, c.key, i)}>{c.custom ? '' : cellValue(r, c.key, i)}</td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={visibleCols.length} className="text-center py-6 text-gray-400">No data</td></tr>
                )}
              </tbody>
            </table>
            {rows.length > 5 && (
              <div className="text-[10px] text-gray-500 px-3 py-2 border-t">Showing first 5 of {rows.length} rows in preview. Full export will include all.</div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
          <div className="text-[11px] text-gray-500">
            {visibleCols.length} of {cols.length} columns will be exported
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-xs rounded bg-gray-200 hover:bg-gray-300 font-semibold"
              data-testid="excel-preview-cancel"
            >Cancel</button>
            <button
              onClick={() => onDownload({ columns: visibleCols.map(c => c.key).join(',') })}
              disabled={visibleCols.length === 0}
              className={`px-4 py-2 text-xs rounded text-white font-bold flex items-center gap-1.5 disabled:opacity-40 ${isPdf ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              data-testid="excel-preview-download"
            >
              <DownloadSimple size={14} weight="bold" /> {isPdf ? 'Download PDF' : 'Download Excel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelExportPreviewModal;
